import React from 'react';
import styled from 'styled-components';
import { Stack } from '../../design-system/components/Layout';
import { Text } from '../../design-system/components/Typography';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { borders } from '../../design-system/tokens/borders';
import FormField from '../common/FormField';

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: ${spacing[4]};
`;

const OptionCard = styled.button<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacing[2]};
  padding: ${spacing[4]} ${spacing[5]};
  border-radius: ${borders.radius.lg};
  border: ${borders.width.base} solid
    ${props => (props.$selected ? props.theme.colors.primary : props.theme.colors.border)};
  background: ${props =>
    props.$selected ? `${props.theme.colors.primary}10` : props.theme.colors.surface};
  cursor: pointer;
  text-align: left;
  transition: all 0.2s ease;
  font-family: ${typography.fontFamily.primary};
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-1px);
  }
`;

const OptionTitle = styled.span`
  font-size: ${typography.fontSize.base};
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text};
`;

const OptionHint = styled.span`
  font-size: ${typography.fontSize.sm};
  color: ${props => props.theme.colors.textSecondary};
  line-height: ${typography.lineHeight.relaxed};
`;

const SelectionBadge = styled.span<{ $selected: boolean }>`
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => (props.$selected ? props.theme.colors.primary : props.theme.colors.textSecondary)};
`;

const TransferFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
  margin-top: ${spacing[2]};
  padding-top: ${spacing[4]};
  border-top: ${borders.width.thin} solid ${props => props.theme.colors.borderLight};
`;

export interface ProfessionalActivitiesSectionProps {
  professionalActivities: { working: boolean; studying: boolean; retired: boolean };
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  t: (key: string) => string;
  /** Clear category-level validation error when the user changes a selection. */
  onSelectionChange?: () => void;
  transferFromIcom?: string;
  previousCardNumber?: string;
}

type ActivityKey = 'working' | 'studying' | 'retired';

const ACTIVITY_OPTIONS: Array<{
  key: ActivityKey;
  titleKey: string;
  hintKey: string;
}> = [
  { key: 'working', titleKey: 'register.categoryEmployee', hintKey: 'register.categoryEmployeeHint' },
  { key: 'studying', titleKey: 'register.categoryStudent', hintKey: 'register.categoryStudentHint' },
  { key: 'retired', titleKey: 'register.categoryRetired', hintKey: 'register.categoryRetiredHint' },
];

export const ProfessionalActivitiesSection: React.FC<ProfessionalActivitiesSectionProps> = ({
  professionalActivities,
  setFormData,
  t,
  onSelectionChange,
  transferFromIcom = '',
  previousCardNumber = '',
}) => {
  const toggleActivity = (key: ActivityKey) => {
    setFormData((prev: any) => {
      const nextActivities = {
        ...(prev.professionalActivities as object),
        [key]: !professionalActivities[key],
      } as { working: boolean; studying: boolean; retired: boolean };
      const updates: Record<string, unknown> = { professionalActivities: nextActivities };
      if (key === 'studying') {
        updates.isGraduateStudent = nextActivities.studying;
        // Deselected student branch discards studies so they are not submitted later.
        if (!nextActivities.studying) updates.studies = [];
      }
      if (key === 'retired') updates.isRetired = nextActivities.retired;
      return { ...prev, ...updates };
    });
    onSelectionChange?.();
  };

  return (
    <Stack direction="column" spacing="md">
      <Text size="sm" color="secondary">
        {t('register.professionalSelectAtLeastOne')}
      </Text>
      <OptionsGrid>
        {ACTIVITY_OPTIONS.map(option => {
          const selected = professionalActivities[option.key];
          return (
            <OptionCard
              key={option.key}
              type="button"
              $selected={selected}
              onClick={() => toggleActivity(option.key)}
              aria-pressed={selected}
            >
              <SelectionBadge $selected={selected}>
                {selected ? t('common.selected') : t('common.select')}
              </SelectionBadge>
              <OptionTitle>{t(option.titleKey)}</OptionTitle>
              <OptionHint>{t(option.hintKey)}</OptionHint>
            </OptionCard>
          );
        })}
      </OptionsGrid>
      <TransferFields>
        <FormField
          id="register-transfer-from-icom"
          label={t('register.transferFromIcom')}
          name="transferFromIcom"
          value={transferFromIcom}
          helperText={t('register.transferFromIcomHint')}
          onChange={e =>
            setFormData((prev: any) => ({ ...prev, transferFromIcom: e.target.value }))
          }
        />
        <FormField
          id="register-previous-card-number"
          label={t('register.previousCardNumber')}
          name="previousCardNumber"
          value={previousCardNumber}
          helperText={t('register.previousCardNumberHint')}
          onChange={e =>
            setFormData((prev: any) => ({ ...prev, previousCardNumber: e.target.value }))
          }
        />
      </TransferFields>
    </Stack>
  );
};

export default ProfessionalActivitiesSection;
